import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dropdown, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { TeamHeader } from '../header/TeamHeader';
import { Loading } from '../../../ui/loading/Loading';
import StratIcon from '../../../../assets/components_icons/StratIcon';
import { zynity_api } from '../../../../config/external-routes';
import { getOrganizationTeam } from '../../../../store/user_organizations/teams/team';
import './StrategiesPage.css';

type StrategyInitiative = {
    id: number;
    title: string;
    description: string | null;
    status: string | null;
    percentComplete: number | null;
    idCategory: number | null;
    idAuthor: number | null;
    dateModified: string | null;
    authors?: number[];
};

// Matches Yii's 4 initiative-list stages (initiatives.php): stored `status` values
// map to distinct display names, badge/left-border colors, and default open/closed
// state — "Preparing" is stored as "Review", everything else matches its own name.
const STAGES: { status: string; label: string; className: string; defaultOpen: boolean }[] = [
    { status: 'Review', label: 'Preparing', className: 'stage-review', defaultOpen: true },
    { status: 'Active', label: 'Active', className: 'stage-active', defaultOpen: true },
    { status: 'Archived', label: 'Archived', className: 'stage-archived', defaultOpen: false },
];

// Deleted initiatives "fall off" the list after this many days (StrategyInitiative::
// DELETED_INITIATIVES_LIST_RETENTION_DAYS in Yii) — filtered client-side by
// dateModified since the API returns every status unfiltered.
const DELETED_RETENTION_DAYS = 30;

const truncateDescription = (description: string | null) => {
    if (!description) {
        return '';
    }
    const stripped = description.replace(/<[^>]*>/g, '').trim();
    return stripped.length > 60 ? `${stripped.substring(0, 60)}…` : stripped;
};

export const StrategiesInitiativesPage = () => {
    const { organization_id, team_id } = useParams();
    const dispatch = useDispatch();
    const authenticatedUser = useSelector((state: any) => state.auth);
    const organizationTeam = useSelector((state: any) => state.organization_team);
    const enabled = !!authenticatedUser?.strategies_enabled;
    // Matches Yii's TeamPeople-based isFacilitator check, which doesn't care about
    // isVisitor — the `members` relation alone excludes isVisitor='Yes' rows (that's
    // what `viewers` is for), which would otherwise exclude a visitor-facilitator
    // from the WS-edit-vs-View action and author-name resolution. Both lists share
    // the same shape, safe to concatenate.
    const teamMembers: any[] = [...(organizationTeam?.data?.members ?? []), ...(organizationTeam?.data?.viewers ?? [])];
    const memberId = (member: any) => (member?.People_id !== undefined && member?.People_id !== null ? member.People_id : member?.id);
    const memberLabel = (member: any) => member.KnownAs || `${member.FirstName ?? ''} ${member.LastName ?? ''}`.trim();
    const isFacilitator = teamMembers.some(
        (m: any) => Number(memberId(m)) === Number(authenticatedUser?.id) && m.isFacilitator === 'Yes'
    );
    const authorNames = (initiative: StrategyInitiative) => {
        const ids = new Set<number>([...(initiative.idAuthor ? [Number(initiative.idAuthor)] : []), ...((initiative.authors ?? []).map(Number))]);
        return Array.from(ids)
            .map((id) => teamMembers.find((m) => Number(memberId(m)) === id))
            .filter(Boolean)
            .map(memberLabel);
    };

    const [initiatives, setInitiatives] = useState<StrategyInitiative[]>([]);
    const [categories, setCategories] = useState<{ id: number; title: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [openStages, setOpenStages] = useState<Record<string, boolean>>(() =>
        Object.fromEntries(STAGES.map((s) => [s.status, s.defaultOpen]))
    );
    const [deletedOpen, setDeletedOpen] = useState(false);
    const categoryName = (id: number | null) => categories.find((c) => c.id === id)?.title ?? null;

    useEffect(() => {
        if (organization_id && team_id) {
            dispatch(getOrganizationTeam({ organization_id, team_id }) as any);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization_id, team_id]);

    useEffect(() => {
        const fetchInitiatives = async () => {
            if (!organization_id || !team_id || !enabled) {
                return;
            }
            setLoading(true);
            try {
                const response = await axios.get(
                    zynity_api.organization_team_strategies
                        .replace('{organization_id}', organization_id)
                        .replace('{team_id}', team_id)
                );
                setInitiatives(response.data.initiatives ?? []);
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Load failed', text: 'Initiatives could not be loaded for this team.' });
            } finally {
                setLoading(false);
            }
        };

        fetchInitiatives();
    }, [organization_id, team_id, enabled]);

    // "Category: X" on each row previously showed the raw idCategory number — a real
    // bug, not a design choice, found once a test worksheet finally had a category
    // assigned. Resolved client-side the same way Authors already is (fetch the
    // team's own category list once, match by id) rather than a backend change.
    useEffect(() => {
        const fetchCategories = async () => {
            if (!organization_id || !team_id || !enabled) {
                return;
            }
            try {
                const response = await axios.get(
                    zynity_api.organization_team_strategies_categories
                        .replace('{organization_id}', organization_id)
                        .replace('{team_id}', team_id)
                );
                setCategories(response.data.categories ?? []);
            } catch (error) {
                setCategories([]);
            }
        };

        fetchCategories();
    }, [organization_id, team_id, enabled]);

    if (!enabled) {
        return (
            <>
                <TeamHeader suffixRoute="/strategies" />
                <div className="container mt-5">
                    <p className="text-muted">This feature is not enabled for your account.</p>
                </div>
            </>
        );
    }

    // Matches Yii's initiativesListSearchHaystack() — title, description, category
    // name, and author names are all searchable, not just the title.
    const matchesFilter = (initiative: StrategyInitiative) => {
        if (filterText.trim() === '') {
            return true;
        }
        const needle = filterText.trim().toLowerCase();
        const haystack = [
            initiative.title,
            initiative.description ?? '',
            categoryName(initiative.idCategory) ?? '',
            ...authorNames(initiative),
        ]
            .join(' ')
            .toLowerCase();
        return haystack.includes(needle);
    };

    const filtered = initiatives.filter(matchesFilter);
    const byDateModifiedDesc = (a: StrategyInitiative, b: StrategyInitiative) =>
        new Date(b.dateModified ?? 0).getTime() - new Date(a.dateModified ?? 0).getTime();

    const retentionCutoff = Date.now() - DELETED_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const deleted = filtered
        .filter((i) => i.status === 'Delete' || i.status === 'Deleted')
        .filter((i) => !i.dateModified || new Date(i.dateModified).getTime() >= retentionCutoff)
        .sort(byDateModifiedDesc);

    const renderCard = (initiative: StrategyInitiative) => {
        const description = truncateDescription(initiative.description);
        const names = authorNames(initiative);
        return (
            <div key={initiative.id} className="initiative-card">
                <div className="initiative-card-content">
                    <div className="initiative-card-title-row">
                        <Link
                            className="initiative-title-btn"
                            to={`/organizations/${organization_id}/teams/${team_id}/strategies/worksheet/${initiative.id}`}
                        >
                            {initiative.title}
                        </Link>
                        <span className="initiative-card-percentage">[{initiative.percentComplete ?? 0}%]</span>
                    </div>
                    {description && <div className="initiative-card-description">{description}</div>}
                    <div className="initiative-card-meta initiative-card-meta--inline">
                        <div className="initiative-card-meta-item initiative-card-meta-topic">
                            <span className="initiative-card-meta-label">Category:</span>
                            <span className="initiative-card-meta-value">
                                {categoryName(initiative.idCategory) ?? 'Unassigned'}
                            </span>
                        </div>
                        <div className="initiative-card-meta-item initiative-card-meta-authors">
                            <span className="initiative-card-meta-label">Authors:</span>
                            <span className="initiative-card-meta-value">{names.join(', ')}</span>
                        </div>
                        <div className="initiative-card-actions">
                            <Link
                                className="initiative-action-btn"
                                title={isFacilitator ? 'Open worksheet' : 'View'}
                                to={`/organizations/${organization_id}/teams/${team_id}/strategies/worksheet/${initiative.id}`}
                            >
                                {isFacilitator ? (
                                    <>
                                        WS&nbsp;<i className="fa fa-pencil-square-o" aria-hidden="true" />
                                    </>
                                ) : (
                                    <i className="fa fa-eye" aria-hidden="true" />
                                )}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <TeamHeader suffixRoute="/strategies" />
            <div className="container mb-5 strategies-app">
                <div className="strategies-app-header d-flex flex-wrap justify-content-between align-items-center mt-4">
                    <div className="d-flex align-items-center gap-2">
                        <StratIcon style={{ width: '30px', height: '30px' }} />
                        <div>
                            <h2 className="mb-0">Strategies</h2>
                            <div className="text-muted small">(Our Future)</div>
                        </div>
                    </div>
                    {/* Confirmed live 2026-08-27+ (`_strategies_app_header.php`'s `swh-center`,
                    re-verified against the actual shared partial rather than inferred): both
                    Categories and Initiatives are always real, clickable links, on the same
                    row as the title and "New Worksheet +" (centered between them) — the
                    current page just gets `is-active`, matching Yii's own
                    `CHtml::link(..., 'is-active')`. */}
                    <div className="d-flex align-items-center gap-2">
                        <Link
                            className="strategies-subnav-link"
                            to={`/organizations/${organization_id}/teams/${team_id}/strategies`}
                        >
                            Categories
                        </Link>
                        <Link
                            className="strategies-subnav-link is-active"
                            to={`/organizations/${organization_id}/teams/${team_id}/strategies/initiatives`}
                        >
                            Initiatives
                        </Link>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Link
                            className="btn btn-teal"
                            to={`/organizations/${organization_id}/teams/${team_id}/strategies/worksheet/new`}
                        >
                            New Worksheet +
                        </Link>
                        <Dropdown>
                            <Dropdown.Toggle className="strategies-ellipsis-btn" aria-label="More actions">
                                •••
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                                <Dropdown.Item
                                    as={Link}
                                    to={`/organizations/${organization_id}/teams/${team_id}/document-library`}
                                >
                                    Docs
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>

                <div className="initiatives-search-toolbar" role="search">
                    <Form.Label className="mb-0">Search</Form.Label>
                    <div className="initiatives-search-input-wrap">
                        <Form.Control
                            id="initiatives-search-input"
                            placeholder="Title, intent, category, authors…"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <Loading />
                ) : (
                    <div className="mt-2">
                        {STAGES.map((stage) => {
                            const rows = filtered
                                .filter((i) => i.status === stage.status)
                                .sort(byDateModifiedDesc);
                            if (rows.length === 0) {
                                return null;
                            }
                            const open = openStages[stage.status];
                            return (
                                <div key={stage.status} className={`initiative-stage initiative-stage-accordion ${stage.className}`}>
                                    <div
                                        className="initiatives-section-header"
                                        role="button"
                                        onClick={() => setOpenStages((current) => ({ ...current, [stage.status]: !current[stage.status] }))}
                                    >
                                        <span className="initiatives-accordion-chevron" aria-hidden="true">
                                            <i className={`fa fa-chevron-${open ? 'down' : 'right'}`} />
                                        </span>
                                        <h5>{stage.label}</h5>
                                        <span className="stage-badge">{rows.length}</span>
                                    </div>
                                    {open && rows.map(renderCard)}
                                </div>
                            );
                        })}

                        <div className="initiative-stage initiative-stage-accordion stage-deleted">
                            <div
                                className="initiatives-section-header"
                                role="button"
                                onClick={() => setDeletedOpen((current) => !current)}
                            >
                                <span className="initiatives-accordion-chevron" aria-hidden="true">
                                    <i className={`fa fa-chevron-${deletedOpen ? 'down' : 'right'}`} />
                                </span>
                                <h5>Deleted</h5>
                                <span className="stage-badge">{deleted.length}</span>
                            </div>
                            {deletedOpen && (
                                <>
                                    <p className="initiatives-deleted-retention-note" role="note">
                                        Deleted initiatives are removed from this list after {DELETED_RETENTION_DAYS} days
                                        (based on last modified date while in deleted status).
                                    </p>
                                    {deleted.map(renderCard)}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
